import type { StoryMediaQuery } from 'types/story';
import type { DesignDefinition } from './types';
import {
	validateDesignPresets,
	normalizeDesignPresetValue,
	getDesignPresetTokenId,
	getDesignPresetTokenIds,
	isDesignPresetTokenCompatible,
} from './presets';
import { collectEffectiveStyleAssignments, type DesignPresetStyles } from './preset-styles';
import { collectDesignPresetRequiredFontFaces } from './preset-fonts';

const CORNERS = ['borderTopLeftRadius', 'borderTopRightRadius', 'borderBottomRightRadius', 'borderBottomLeftRadius'];
export type PresetOperationResult = { styles: DesignPresetStyles; designPresetId?: string };

export function assertDesignPresetBinding(
	design: DesignDefinition | undefined,
	presetId: string,
	mediaQuery: StoryMediaQuery
) {
	const source = design?.presets?.find(item => item.id === presetId);
	if (!design || !source) throw new Error(`Unknown Story design preset: ${presetId}.`);
	const preset = validateDesignPresets([source])[0];
	if (!Object.keys(preset.properties).length) throw new Error('An empty design preset cannot be bound.');
	const collected = collectEffectiveStyleAssignments({
		design: { ...design, presets: [preset] },
		presetId,
		mediaQuery,
		layers: [],
	});
	if (collected.diagnostics.length) {
		const issue = collected.diagnostics[0];
		throw new Error(`Invalid design preset ${presetId}: ${issue.code} ${issue.tokenId ?? issue.platform ?? ''}.`);
	}
	const tokens = design.tokens.collections.flatMap(collection => collection.tokens);
	for (const [property, assignment] of Object.entries(preset.properties)) {
		for (const value of [assignment.base, ...Object.values(assignment.platforms ?? {})]) {
			for (const id of getDesignPresetTokenIds(value)) {
				const token = tokens.find(item => item.id === id)!;
				if (!isDesignPresetTokenCompatible(property, token.type))
					throw new Error(`Token ${id} is incompatible with ${property}.`);
				// A token in part of a value, such as a shadow's color, is checked as that part by its type
				if (getDesignPresetTokenId(value) !== id) continue;
				for (const literal of [token.value.base, ...(token.value.overrides ?? []).map(item => item.value)])
					normalizeDesignPresetValue(property as keyof typeof preset.properties, literal);
			}
		}
	}
	return preset;
}

export function planDesignPresetBinding({
	design,
	presetId,
	styles,
	mediaQuery,
	validateFont,
}: {
	design?: DesignDefinition;
	presetId: string;
	styles: DesignPresetStyles;
	mediaQuery: StoryMediaQuery;
	validateFont: (family: string, weight?: number) => void;
}): PresetOperationResult {
	const preset = assertDesignPresetBinding(design, presetId, mediaQuery);
	const next = structuredClone(styles);
	const managed = new Set(Object.keys(preset.properties));
	if (managed.has('borderRadius')) CORNERS.forEach(corner => managed.add(corner));
	const partialRadius = !managed.has('borderRadius') && CORNERS.some(corner => managed.has(corner));
	for (const values of Object.values(next.defaultState ?? {})) {
		if (partialRadius && values.borderRadius !== undefined) {
			for (const corner of CORNERS) if (values[corner] === undefined) values[corner] = values.borderRadius;
			delete values.borderRadius;
		}
		for (const property of managed) delete values[property];
	}
	if (design && (preset.properties.fontFamily || preset.properties.fontWeight)) {
		for (const face of collectDesignPresetRequiredFontFaces({
			design,
			presetId,
			mediaQuery,
			layers: [{ owner: 'element', styles: next }],
		}))
			validateFont(face.family, face.weight);
	}
	return { styles: next, designPresetId: presetId };
}

export function planDesignPresetDetach({
	design,
	presetId,
	styles,
	mediaQuery,
}: {
	design?: DesignDefinition;
	presetId: string;
	styles: DesignPresetStyles;
	mediaQuery: StoryMediaQuery;
}): PresetOperationResult {
	assertDesignPresetBinding(design, presetId, mediaQuery);
	return {
		styles: collectEffectiveStyleAssignments({
			design,
			presetId,
			mediaQuery,
			layers: [{ owner: 'element', styles }],
		}).styles,
	};
}
